import { getUserPayPalConfig } from "../utils/paypal";
import {
  getBooksPaymentMethodOptions,
  getUserPaymentMethodOptions,
  usePaymentMethods,
  type PaymentMethodAvailability,
  type PaymentMethodOptions,
  type UserPaymentSource,
} from "./usePaymentMethods";

type PayPalAvailability = {
  config: ReturnType<typeof getUserPayPalConfig>;
  isAvailable: boolean;
};

export const usePayPalAvailability = (
  user: UserPaymentSource,
  defaults?: PaymentMethodOptions,
): PayPalAvailability => {
  const config = getUserPayPalConfig(user);
  const { isAvailable } = usePaymentMethods(
    getUserPaymentMethodOptions(user, defaults),
  );

  return {
    config,
    isAvailable: isAvailable("paypal") && !!config,
  };
};

export const useBooksPayPalAvailability = (
  user: UserPaymentSource,
): PayPalAvailability => {
  const config = getUserPayPalConfig(user);
  const { isAvailable } = usePaymentMethods(getBooksPaymentMethodOptions(user));

  return {
    config,
    isAvailable: isAvailable("paypal") && !!config,
  };
};

export const usePayPalAwareUserPaymentMethods = (
  user: UserPaymentSource,
  defaults?: PaymentMethodOptions,
): PaymentMethodAvailability & {
  paypalConfig: PayPalAvailability["config"];
  isPayPalAvailable: boolean;
} => {
  const paypalConfig = getUserPayPalConfig(user);
  const paymentMethods = usePaymentMethods(
    getUserPaymentMethodOptions(user, defaults),
  );
  const isPayPalAvailable =
    paymentMethods.isAvailable("paypal") && !!paypalConfig;
  const available = paymentMethods.available.filter(
    (method) => method !== "paypal" || isPayPalAvailable,
  );

  return {
    available,
    isAvailable: (method) =>
      method === "paypal" ? isPayPalAvailable : available.includes(method),
    paypalConfig,
    isPayPalAvailable,
  };
};
